import { FC, HTMLAttributes, useState } from "react";
import clsx from "classnames";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "../../core/Dialog/Dialog";
import { Typography } from "../../core/Typography/Typography";
import { Button } from "../../core/Button/Button";
import { TypographyVariant } from "../../../constants/typography";
import { isTV } from "../../../utils/platform";
import { YtbVideoPlayer } from "../YtbVideoPlayer/YtbVideoPlayer";
import { VideoPopupLayout } from "../VideoPopupLayout";
import { useAppSelector } from "../../hooks";
import { selectIsDesktop } from "../../../store/slices/appState";
import { useSubscriptionNavigate } from "../../../hooks/useSubscriptionNavigate";
import { selectIsAuthenticated } from "@onmo/auth";
import { STEAM_LOGIN_PAGE } from "../../../constants/gamePack";
import { PageRoutes } from "../../../constants/routes";
import { GAME_TO_LINK_STEAM } from "../../../constants/gamePlay";
import { getStatusInfo } from "../../../models/user";
import { useStyles } from "./PopupLinkSteam.style";
import { useAAASubscription } from "../../../hooks/useAAASubscription";

interface IPopupLinkSteamProps {
  isOpen: boolean;
  onClose?: () => void;
  isLinked?: boolean;
  link?: string;
}

export const PopupLinkSteam: FC<
  HTMLAttributes<HTMLDivElement> & IPopupLinkSteamProps
> = (props) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const navigateSubscription = useSubscriptionNavigate();
  const { isOpen, link, isLinked, onClose } = props;
  const isDesktop = useAppSelector(selectIsDesktop);
  const [isShowQr, setIsShowQr] = useState(false);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { data: dataSubscription } = useAAASubscription({
    skip: !isAuthenticated,
  });
  const status = dataSubscription?.me?.subscription?.status;
  const isRecurring = Boolean(dataSubscription?.me?.subscription?.isRecurring);
  const validUntil = dataSubscription?.me?.subscription?.validUntil;
  const statusInfo = getStatusInfo(status, validUntil, isRecurring);

  const onCreateSteam = () => {
    if (!isTV && !isLinked) {
      window.open(STEAM_LOGIN_PAGE);
    } else if (isShowQr) {
      setIsShowQr(false);
    } else if (isLinked) {
      if (
        statusInfo === "subscribed" ||
        statusInfo === "expire-soon" ||
        statusInfo === "grace"
      ) {
        navigate(`${PageRoutes.GamePlay}/${GAME_TO_LINK_STEAM}`);
      } else {
        navigateSubscription(PageRoutes.Paywall);
      }
    } else {
      setIsShowQr(true);
    }
  };

  const onCancel = () => {
    onClose?.();
    setIsShowQr(false);
  };

  const getSubmitLabel = () => {
    if (isShowQr) {
      return "Back to Video";
    } else if (isLinked) {
      return "Link";
    } else {
      return "Create";
    }
  };

  const renderContent = () => {
    if (isShowQr) {
      return (
        <>
          <Typography variant={TypographyVariant.BodyB3Medium}>
            Please scan the QR code below to create Steam account on your
            mobile.{" "}
          </Typography>
          <QRCodeCanvas
            value={STEAM_LOGIN_PAGE}
            size={140}
            bgColor="#FFFFFF"
            fgColor="#000000"
            level="L"
            marginSize={3}
            className={classes.qrCode}
          />
        </>
      );
    } else {
      return (
        link && (
          <YtbVideoPlayer
            url={link}
            loop
            title="Video Steam"
            className={classes.video}
          />
        )
      );
    }
  };

  // Layout: TV (QR always shown when creating) or Desktop (no QR)
  if (isTV || isDesktop) {
    const title = isLinked ? "Link Steam Account" : "Create Steam Account";
    const actionLabel = isLinked ? "Link" : "Create";
    // QR only appears on TV in the "create" flow — linked users skip QR entirely.
    const showQrInline = isTV && !isLinked;
    const description = showQrInline
      ? "Please scan the QR code to create Steam account on your mobile."
      : isLinked
      ? "Link your account to access your Steam games instantly on ONMO+."
      : "Watch this quick guide to set up your free Steam account and start building your library.";

    return (
      <VideoPopupLayout
        open={isOpen}
        onClose={onCancel}
        videoUrl={link}
        videoTitle="Video Steam"
        thumbnailQuality="maxres"
        qr={
          showQrInline
            ? { value: STEAM_LOGIN_PAGE, show: true, size: 80 }
            : undefined
        }
        title={title}
        description={
          <Typography
            variant={TypographyVariant.BodyB3Medium}
            className={classes.descLarge}
          >
            {description}
          </Typography>
        }
        actions={
          showQrInline ? (
            <Button
              variant="outlined"
              color="secondary"
              onClick={onCancel}
              id="close-dialog"
              data-button-sound="back"
              data-gamepad="default-selected"
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                color="secondary"
                onClick={onCancel}
                id="close-dialog"
                data-button-sound="back"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={onCreateSteam}
                id="link-steam-main"
                data-gamepad="default-selected"
              >
                {actionLabel}
              </Button>
            </>
          )
        }
      />
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      mainAction={{
        text: getSubmitLabel(),
        onClick: onCreateSteam,
        id: "unsubscribe",
      }}
      secondAction={{
        text: "Cancel",
        onClick: onCancel,
        id: "close-dialog",
      }}
      rootClassName={classes.root}
      className={classes.contentPopup}
    >
      <Typography
        variant={TypographyVariant.HeadingH5Bold}
        className={classes.title}
      >
        {isLinked ? "Link Steam Account" : "Create Steam Account"}
      </Typography>
      <div className={clsx(classes.content, isShowQr && classes.contentQr)}>
        {renderContent()}
      </div>
    </Dialog>
  );
};
